import { useEffect, useMemo, useState, useRef } from "react";
import { getChainProof } from "../services/apiV2";
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";

/* --- CSS STYLES (Injected for M26) --- */
const styles = `
  /* Keyframes */
  @keyframes flashGreen {
    0% { background-color: rgba(0, 255, 148, 0.8); }
    100% { background-color: transparent; }
  }
  @keyframes pulseGreen {
    0% { box-shadow: 0 0 0 rgba(0, 255, 148, 0); }
    50% { box-shadow: 0 0 10px rgba(0, 255, 148, 0.5); }
    100% { box-shadow: 0 0 0 rgba(0, 255, 148, 0); }
  }
  @keyframes shake {
    0% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    50% { transform: translateX(5px); }
    75% { transform: translateX(-5px); }
    100% { transform: translateX(0); }
  }
  @keyframes scan {
    0% { background-position: -100% 0; }
    100% { background-position: 200% 0; }
  }
  
  /* Demo Mode Classes */
  .demo-new-row {
    animation: flashGreen 0.5s ease-out;
  }
  .demo-hover-row td {
    border-top: 1px solid rgba(0, 224, 255, 0.3) !important;
    border-bottom: 1px solid rgba(0, 224, 255, 0.3) !important;
    transform: scaleY(1.02);
    transition: transform 0.1s;
  }
  .demo-link-active {
    text-shadow: 0 0 8px #00E0FF;
    color: #00E0FF !important;
  }
  
  /* Quantum Trace */
  .quantum-invalid {
    filter: blur(2px) grayscale(0.8);
    opacity: 0.5;
    transition: filter 0.5s, opacity 0.5s;
  }
  .quantum-valid {
    box-shadow: inset 0 0 10px rgba(0, 255, 148, 0.1);
  }
  
  /* Tamper Cascade (Demo Mode) */
  .tamper-cascade-demo {
    transition: background-color 0.1s;
  }
`;

// --- HELPER COMPONENTS ---

// Signature Reveal (Demo Only)
// Shows scrambling characters before settling on the real hash
const SignatureReveal = ({ hash, isDemo, isNew }) => {
    const [display, setDisplay] = useState(hash);

    useEffect(() => {
        if (!isDemo || !isNew) {
            setDisplay(hash);
            return;
        }

        // Scramble effect
        let steps = 0;
        const maxSteps = 10;
        const chars = "ABCDEF0123456789";

        const interval = setInterval(() => {
            if (steps >= maxSteps) {
                clearInterval(interval);
                setDisplay(hash);
            } else {
                let scrambled = "";
                for (let i = 0; i < 8; i++) scrambled += chars[Math.floor(Math.random() * chars.length)];
                setDisplay(scrambled + "...");
                steps++;
            }
        }, 30);

        return () => clearInterval(interval);
    }, [hash, isDemo, isNew]);

    // Show full hash if not revealing, or partial if revealing
    return <span style={{ fontFamily: "monospace" }}>{display === hash ? hash.substring(0, 16) + "..." : display}</span>;
}


// --- CORE LOGIC (M23 + M26) ---

const getRowValue = (row, field, tamperMap) => {
    const tampered = tamperMap[row.id];
    if (tampered && field in tampered) {
        return tampered[field];
    }
    if (field === "event_hash") return row.event_hash || "";
    if (field === "prev_event_hash") return row.prev_event_hash || "";
    return "";
};

const computeContinuity = (rows, tamperMap) => {
    const n = rows.length;
    if (n === 0) return [];
    const validity = new Array(n).fill(true);
    validity[n - 1] = true;

    for (let i = n - 2; i >= 0; i--) {
        const current = rows[i];
        const next = rows[i + 1];
        const prevHash = getRowValue(current, "prev_event_hash", tamperMap);
        const nextEventHash = getRowValue(next, "event_hash", tamperMap);

        const linkValid = (prevHash === nextEventHash);
        validity[i] = linkValid && validity[i + 1];
    }
    return validity;
};

export default function V2Explorer() {
    const [rows, setRows] = useState([]);
    const [fullResponse, setFullResponse] = useState(null);
    const [status, setStatus] = useState("LOADING");
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [limit, setLimit] = useState(50);

    // M23 Logic State
    const [tamperMap, setTamperMap] = useState({});
    const [hoverId, setHoverId] = useState(null);
    const [verifiedMap, setVerifiedMap] = useState({});

    // M26 Control Room State
    const [demoMode, setDemoMode] = useState(false);
    const [quantumMode, setQuantumMode] = useState(false);
    const prevHeadIdRef = useRef(null); // Ref to track previous head ID without re-renders
    const [newBlockIds, setNewBlockIds] = useState(new Set()); // IDs of newly added blocks for animation

    // Inline Audit State
    const [pastedProof, setPastedProof] = useState("");
    const [inlineResult, setInlineResult] = useState(null);

    const loadData = async () => {
        try {
            const res = await getChainProof(limit);
            // Strict sorting: DESC by ID
            const sortedEvents = (res.events || []).sort((a, b) => b.id - a.id);

            // M26: Detect New Blocks
            if (sortedEvents.length > 0) {
                const currentHeadId = sortedEvents[0].id;
                const prevHeadId = prevHeadIdRef.current;

                if (prevHeadId !== null && currentHeadId > prevHeadId) {
                    // Identify new blocks
                    const newIds = new Set();
                    for (let evt of sortedEvents) {
                        if (evt.id > prevHeadId) newIds.add(evt.id);
                        else break; // Sorted DESC, so we can stop
                    }
                    setNewBlockIds(newIds);

                    // Clear "New" status after animation
                    setTimeout(() => setNewBlockIds(new Set()), 2000);
                }
                prevHeadIdRef.current = currentHeadId;
            }

            setRows(sortedEvents);
            setFullResponse(res);
            setStatus("READY");
        } catch (err) {
            if (err.message === "RATE_LIMITED") {
                setStatus("RATE_LIMITED");
                if (autoRefresh) setAutoRefresh(false);
            } else {
                setStatus(err.message || "ERROR");
            }
        }
    };

    useEffect(() => {
        loadData();
        let interval;
        if (autoRefresh) {
            interval = setInterval(loadData, 10000);
        }
        return () => clearInterval(interval);
    }, [autoRefresh, limit]);

    // Recalculate continuity
    const continuity = useMemo(() => computeContinuity(rows, tamperMap), [rows, tamperMap]);

    // Actions
    const simulateTamper = (rowId) => {
        setTamperMap(prev => ({
            ...prev,
            [rowId]: { event_hash: "CORRUPTED_" + Date.now().toString(16) }
        }));
    };

    const resetTamper = () => {
        setTamperMap({});
        setVerifiedMap({});
    };

    const toggleVerify = (rowId, isValid) => {
        const result = isValid ? "VERIFIED" : "BROKEN";
        setVerifiedMap(prev => ({ ...prev, [rowId]: result }));
        // Demo Mode: Add shake effect or something if broken? 
        // For now, keeps generic logic.
    };

    // M26: Toggle Modes
    const toggleDemo = () => setDemoMode(p => !p);
    const toggleQuantum = () => setQuantumMode(p => !p);

    // Columns
    const columns = useMemo(() => [
        {
            id: "cci",
            header: "CCI",
            cell: (info) => {
                const isValid = continuity[info.row.index];
                const color = isValid ? "#00FF94" : "#FF003C";
                return (
                    <div
                        title={isValid ? "Cryptographic Continuity: OK" : "Chain Broken Downstream"}
                        style={{
                            width: 12, height: 12, borderRadius: "50%",
                            background: color,
                            boxShadow: demoMode || isValid ? `0 0 8px ${color}` : "none",
                            transition: "background 0.3s, box-shadow 0.3s",
                            margin: "auto"
                        }}
                    />
                );
            },
            size: 50,
        },
        {
            header: "ID",
            accessorKey: "id",
            cell: info => <span style={{ opacity: 0.7 }}>#{info.getValue()}</span>
        },
        {
            header: "Timestamp",
            accessorFn: (row) => row.event?.timestamp,
        },
        {
            header: "Event Hash (Current)",
            accessorFn: (row) => getRowValue(row, "event_hash", tamperMap),
            cell: (info) => {
                const rowIndex = info.row.index;
                // Highlight if WE are the "Next" block of the hovered row (i.e. we are i+1, hover is i)
                // Wait: row[i] has prev_hash. row[i+1] has event_hash.
                // If I hover row[i], I want to highlight row[i].prev and row[i+1].event.
                // So if I AM row[i+1] (this cell), and hoverId is row[i].

                // My Index = rowIndex.
                // Hovered Index check:
                const prevRow = rowIndex > 0 ? rows[rowIndex - 1] : null;
                const isTarget = prevRow && prevRow.id === hoverId;
                const isNew = newBlockIds.has(info.row.original.id);

                let className = "";
                if (demoMode && isTarget) className = "demo-link-active";

                return (
                    <span className={className} style={{
                        fontFamily: "monospace",
                        transition: "color 0.2s, text-shadow 0.2s",
                        fontWeight: isTarget ? "bold" : "normal",
                        color: !demoMode && isTarget ? "#00E0FF" : "inherit" // Prod glow
                    }}>
                        <SignatureReveal hash={info.getValue()} isDemo={demoMode} isNew={isNew} />
                    </span>
                );
            }
        },
        {
            header: "Prev Hash (Link)",
            accessorFn: (row) => getRowValue(row, "prev_event_hash", tamperMap),
            cell: (info) => {
                const isHovered = info.row.original.id === hoverId;
                const isNew = newBlockIds.has(info.row.original.id);

                let className = "";
                if (demoMode && isHovered) className = "demo-link-active";

                return (
                    <span className={className} style={{
                        fontFamily: "monospace",
                        transition: "color 0.2s, text-shadow 0.2s",
                        fontWeight: isHovered ? "bold" : "normal",
                        color: !demoMode && isHovered ? "#00E0FF" : "inherit", // Prod glow
                        opacity: isHovered ? 1 : 0.6
                    }}>
                        {info.getValue().substring(0, 16)}...
                    </span>
                );
            }
        },
        {
            id: "audit",
            header: "Audit Tools",
            cell: (info) => {
                const row = info.row.original;
                const isValid = continuity[info.row.index];
                const status = verifiedMap[row.id];

                return (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <button
                            onClick={(e) => { e.stopPropagation(); toggleVerify(row.id, isValid); }}
                            style={{
                                background: "none", border: "1px solid #333",
                                color: isValid ? "#00FF94" : "#FF003C",
                                borderRadius: 4, padding: "2px 6px",
                                cursor: "pointer", fontSize: "0.75rem",
                                transition: "all 0.2s"
                            }}
                        >
                            VERIFY
                        </button>

                        <button
                            onClick={(e) => { e.stopPropagation(); simulateTamper(row.id); }}
                            title="Simulate Tampering"
                            style={{
                                background: "none", border: "1px solid #333",
                                color: "#FF003C", borderRadius: 4, padding: "2px 6px",
                                cursor: "pointer", fontSize: "0.75rem"
                            }}
                        >
                            ⚠️
                        </button>

                        {status === "VERIFIED" && <span style={{ color: "#00FF94" }}>✔</span>}
                        {status === "BROKEN" && <span style={{ color: "#FF003C" }}>✖</span>}
                    </div>
                );
            }
        }
    ], [rows, tamperMap, continuity, hoverId, verifiedMap, demoMode, newBlockIds]);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 20 } },
    });

    // Inline verify
    const verifyPastedProof = () => {
        try {
            let hashToVerify = null;
            const input = pastedProof.trim();
            try {
                const parsed = JSON.parse(input);
                hashToVerify = parsed?.integrity?.chain_head ||
                    parsed?.integrity?.archive_hash ||
                    (Array.isArray(parsed) ? parsed[0]?.event_hash : null) ||
                    parsed?.event_hash;
            } catch (e) {
                if (input.length >= 64) hashToVerify = input;
            }
            const latestRow = rows[0];
            if (!hashToVerify || !latestRow) { setInlineResult("INVALID_DATA"); return; }
            if (hashToVerify.toLowerCase() === latestRow.event_hash.toLowerCase()) {
                setInlineResult("MATCH");
            } else {
                setInlineResult("MISMATCH");
            }
        } catch (err) { setInlineResult("ERROR"); }
    };

    const exportJson = () => {
        if (!fullResponse) return;
        const blob = new Blob([JSON.stringify(fullResponse, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `oracle_v2_export_${Date.now()}.json`;
        a.click();
    };

    return (
        <div style={{ color: "white", fontFamily: "'Inter', sans-serif" }}>
            <style>{styles}</style>

            {/* Header / Control Room */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <div>
                    <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10, letterSpacing: 1 }}>
                        <span style={{ color: "#00FF94", textShadow: demoMode ? "0 0 10px #00FF94" : "none" }}>●</span>
                        SENTINEL CONTROL ROOM
                    </h2>
                    <div style={{ opacity: 0.6, fontSize: "0.85rem", marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
                        Client-side Cryptographic Auditor • {rows.length} Events Synced
                    </div>
                </div>

                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginRight: 8 }}>MODES:</div>

                    <button
                        onClick={toggleQuantum}
                        style={{
                            background: quantumMode ? "rgba(138, 43, 226, 0.2)" : "#111",
                            color: quantumMode ? "#E0B0FF" : "#666",
                            border: `1px solid ${quantumMode ? "#E0B0FF" : "#333"}`,
                            padding: "6px 12px", borderRadius: 4, cursor: "pointer",
                            fontSize: "0.8rem", fontWeight: "bold",
                            boxShadow: quantumMode ? "0 0 8px rgba(138, 43, 226, 0.4)" : "none"
                        }}>
                        QUANTUM TRACE {quantumMode ? "ON" : "OFF"}
                    </button>

                    <button
                        onClick={toggleDemo}
                        style={{
                            background: demoMode ? "rgba(0, 224, 255, 0.2)" : "#111",
                            color: demoMode ? "#00E0FF" : "#888",
                            border: `1px solid ${demoMode ? "#00E0FF" : "#333"}`,
                            padding: "6px 12px", borderRadius: 4, cursor: "pointer",
                            fontSize: "0.8rem", fontWeight: "bold",
                            boxShadow: demoMode ? "0 0 10px rgba(0, 224, 255, 0.4)" : "none"
                        }}>
                        {demoMode ? "⚡ DEMO MODE" : "🔒 PRODUCTION"}
                    </button>
                </div>
            </div>

            {/* Sub-Header Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={resetTamper} style={{ background: "#222", color: "white", border: "1px solid #444", padding: "6px 12px", borderRadius: 4, cursor: "pointer", fontSize: "0.8rem" }}>
                        ↺ RESET SIMULATION
                    </button>
                    <button
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        style={{
                            background: autoRefresh ? "#003322" : "#220000",
                            color: autoRefresh ? "#00FF94" : "#FF5555",
                            border: `1px solid ${autoRefresh ? "#00FF94" : "#FF5555"}`,
                            padding: "6px 12px", borderRadius: 4, cursor: "pointer", fontSize: "0.8rem"
                        }}>
                        {autoRefresh ? "● LIVE FEED" : "⏸ PAUSED"}
                    </button>
                    {/* Inline Audit Quick Input */}
                    <div style={{ display: "flex", gap: 8, background: "#111", padding: 2, borderRadius: 4, border: "1px solid #333" }}>
                        <input
                            value={pastedProof}
                            onChange={e => setPastedProof(e.target.value)}
                            placeholder="Inline Audit (Hash/JSON)..."
                            style={{ background: "transparent", border: "none", color: "white", padding: "4px 8px", fontFamily: "monospace", width: 200, fontSize: "0.8rem" }}
                        />
                        <button onClick={verifyPastedProof} style={{ background: "#333", color: "white", border: "none", padding: "0 8px", cursor: "pointer", fontSize: "0.7rem" }}>CHECK</button>
                        {inlineResult && <span style={{ padding: "0 8px", display: "flex", alignItems: "center", color: inlineResult === "MATCH" ? "#00FF94" : "#FF003C", fontSize: "0.8rem", fontWeight: "bold" }}>{inlineResult}</span>}
                    </div>
                </div>
                <button onClick={exportJson} style={{ background: "transparent", color: "#888", border: "none", cursor: "pointer", fontSize: "0.8rem", textDecoration: "underline" }}>
                    Export Data
                </button>
            </div>

            {/* Table */}
            <div style={{ border: "1px solid #333", borderRadius: 8, overflow: "hidden", boxShadow: demoMode ? "0 0 20px rgba(0,0,0,0.5)" : "none" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ background: "#111" }}>
                        {table.getHeaderGroups().map(hg => (
                            <tr key={hg.id}>
                                {hg.headers.map(h => (
                                    <th key={h.id} style={{ padding: 12, textAlign: "left", fontSize: "0.75rem", color: "#666", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #333" }}>
                                        {flexRender(h.column.columnDef.header, h.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row, index) => {
                            const isNew = newBlockIds.has(row.original.id);
                            const isHovered = row.original.id === hoverId;
                            const isValid = continuity[row.index];

                            // Quantum Trace Logic
                            let quantumClass = "";
                            if (quantumMode) {
                                quantumClass = isValid ? "quantum-valid" : "quantum-invalid";
                            }

                            // Demo Propagated Delay (for Red Trigger)
                            // If row is invalid, we want a delay based on how far 'down' it is from the break?
                            // Actually, calculating the exact "cause" row is hard without searching, 
                            // but we can use Index to stagger visual updates if we wanted.
                            // For M26, we stick to CSS classes.

                            return (
                                <tr
                                    key={row.id}
                                    onMouseEnter={() => setHoverId(row.original.id)}
                                    onMouseLeave={() => setHoverId(null)}
                                    className={`${isNew && demoMode ? "demo-new-row" : ""} ${isHovered && demoMode ? "demo-hover-row" : ""} ${quantumClass}`}
                                    style={{
                                        borderBottom: "1px solid #222",
                                        background: (() => {
                                            if (!isValid && demoMode) return "rgba(255, 0, 60, 0.05)"; // Red tint for broken chain in Demo
                                            if (isNew && !demoMode) return "rgba(0, 255, 148, 0.05)"; // Prod subtle pulse
                                            return "transparent";
                                        })(),
                                        transition: demoMode ? "background 0.3s ease-out" : "none"
                                    }}
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <td key={cell.id} style={{ padding: "10px 12px", fontSize: "0.85rem" }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: 16, opacity: 0.4, fontSize: "0.75rem", textAlign: "center" }}>
                Oracle V2 System • Mode: {demoMode ? "DEMONSTRATION (Visual Amplified)" : "PRODUCTION (Minimalist)"} • Quantum Trace: {quantumMode ? "ACTIVE" : "STANDBY"}
            </div>
        </div>
    );
}
